import Context from "shared/util/Context";
import UrlHelpers from "shared/util/UrlHelpers";
import { scale } from "shared/util/NumberHelpers";
import _strf from "shared/util/_strf";
import { SECONDS_BETWEEN_REANSWER } from "apps/questions/util/constants";

import {
	FILTERS,
	IMPORTANT_HEADER_TRIGGER,
} from "apps/profile_questions/util/constants";

/**
 * Get a username/userid from a profile url.
 *
 * @param {string} _url - the url
 * @return {string} - the username or userid found in the url, null if not found
 */
export const getUserFromUrlString = (_url) => {
	// y'all gotta pass the url
	if (typeof _url !== "string") {
		return null;
	}

	const url = _url.replace(/^\//, "");
	const defaultResponse = Context.get("userid");

	// if there are no slashes, this isn't a url (or at least a url that we want
	// to mess with)
	if (url.indexOf("/") < 0) {
		return defaultResponse;
	}

	const pieces = url.split("/");

	// if we aren't on profile, we can't get a username... so this means we are
	// on the `/questions` page, which is actually self-view
	if (pieces[0] !== "profile") {
		return defaultResponse;
	}

	return pieces[1];
};

/**
 * Programatically but deterministically get a placeholder width, using the
 * length of the question as a key.
 *
 * @param {string} text - the question text
 * @return {int} - a number to use as a placeholder width
 */
export const getPlaceholderWidth = (text = "") => {
	return scale({
		input: text.length,
		inMin: 40,
		inMax: 116,
		outMin: 30,
		outMax: 70
	});
};

const getNowInSeconds = () => {
	return Math.round(Date.now() / 1000);
};

/**
 * Get the number of seconds between a timestamp and now (or optionally another
 * timestamp, for testing).
 *
 * @param {number} timestamp - the timestamp (okws-style, seconds since
 *   1970) when the question was answered
 * @param {number} [now] - optionally pass the time that "now" is, in seconds
 *   since 1970, for testing
 * @return {number} - the number of seconds between the timestamp and now
 */
export const getSecondsFromNow = (timestamp, now) => {
	let nowInSeconds;

	if (now) {
		nowInSeconds = now;
	} else {
		nowInSeconds = getNowInSeconds();
	}

	return nowInSeconds - timestamp;
};

/**
 * Convert a number of seconds to a string of hours.
 *
 * @param {nubmer} seconds - the number of seconds to convert
 * @return {string} - the number of hours, e.g. "2hrs"
 */
export const secondsToHourString = (seconds) => {
	const hours = Math.round(seconds / 60 / 60);
	let suffix = hours === 1 ? "hr" : "hrs"; // no _str necessary; hours are hours

	if (hours === 0) {
		const min = Math.round(seconds / 60);
		suffix = "min";

		return `${min}${suffix}`;
	} else {
		return `${hours}${suffix}`;
	}
};

/**
 * Get a string representing how many hours are left until you can reanswer a
 * question.
 *
 * @param {number} timeAnswered - the time the question was answered (in seconds
 *   since the epoch)
 * @return {string} - the number of hours in the format "2hrs"
 */
export const getHourStringFromTimeAnswered = (timeAnswered) => {
	// get the number of seconds until the reanswer time. this means get the
	// reanswer time (time answered plus seconds between reanswer) and subtract
	// the current timestamp to get the difference.
	const timeForReanswering = timeAnswered + SECONDS_BETWEEN_REANSWER;
	return secondsToHourString(timeForReanswering - getNowInSeconds());
};

/**
 * Given a timestamp of when a question was answered, return if it can be
 * re-answered.
 *
 * @param {number} timeAnswered - the timestamp (okws-style, seconds since
 *   1970) when the question was answered
 * @param {number} [now] - optionally pass the time that "now" is, in seconds
 *   since 1970, for testing
 * @return {bool} - true if you can reanswer the question
 */
export const canReanswer = (timeAnswered, now) => {
	// if there's no time passed, then it's unanswered
	if (!timeAnswered) {
		return true;
	}

	const secondsToReanswer = getSecondsFromNow(timeAnswered, now);

	return secondsToReanswer > SECONDS_BETWEEN_REANSWER;
};

/**
 * As you answer more questions, your highest possible match percentage goes
 * up. This is the formula, passed down to us by our forebearers (aka copied
 * from the old bigdig service in the okcupid repo).
 *
 * @param {number} answerCount - the number of answers a user has
 * @return {number} - the maximum match percentage for that user
 */
export const getMaxPercentFromAnswerCount = (answerCount = 0) => {
	if (answerCount === 0) {
		return 0;
	}

	return Math.round((1 - 1.0 / answerCount) * 1000) / 10;
};

/**
 * Given a question object in selfview, figure out to which categories it
 * belongs.
 *
 * @param {Object} question - the question
 * @return {Array} - a list of categories into which the question falls
 */
export const getSelfviewQuestionCategories = (question) => {
	const res = [];

	if (!question || !question.target) {
		return res;
	}

	if (question.target.importance <= IMPORTANT_HEADER_TRIGGER) {
		res.push(FILTERS.IMPORTANT);
	}

	if (question.target.public) {
		res.push(FILTERS.PUBLIC);
	} else {
		res.push(FILTERS.PRIVATE);
	}

	if (question.target.note) {
		res.push(FILTERS.EXPLAINED);
	}

	if (question.target.skipped) {
		res.push(FILTERS.SKIPPED);
	}

	return res;
};

/**
 * Given a question list and a single question, return a new list with the
 * single question replaced with the new version.
 *
 * @param {Array} questions - a list of questions
 * @param {Object} updatedQuestion - the new question that you want added in
 * @return {Array} - the updated list of questions
 */
export const mapQuestionList = (questions, updatedQuestion) => {
	if (
		!questions ||
		!Array.isArray(questions) ||
		!updatedQuestion ||
		!(updatedQuestion.question && updatedQuestion.question.id)
	) {
		return questions;
	}

	return questions.map((question) => {
		if (updatedQuestion.question.id === question.question.id) {
			return updatedQuestion;
		} else {
			return question;
		}
	});
};

/**
 * Update the match percent in the mobile web header, if we are on mobile web
 * and have found the header.
 * @param {number|string} percent - the match percent
 */
export const updateMobileWebMatchPercentage = (percent) => {
	const div = document.getElementById("profile-heading-match-percent");

	if (
		Context.get("platform") !== "MOBILE_SITE" ||
		!div ||
		typeof percent === "undefined" ||
		percent === null
	) {
		return;
	}

	div.innerHTML = _strf("$percent% Match", { percent });
};

/**
 * Safely get the value of the filter_id url param.
 * @return {number|undefined} the value of the filter url param, if present
 */
export const getFilterUrlParam = () => {
	if (!window || !window.location) {
		return undefined;
	}

	const filterValues = {};

	Object.keys(FILTERS).forEach((filter) => {
		filterValues[FILTERS[filter]] = true;
	});

	const urlParam = UrlHelpers.getParam("filter_id");

	if (urlParam && urlParam.match(/^\d+$/) && filterValues[urlParam]) {
		return parseInt(urlParam, 10);
	} else {
		return undefined;
	}
};



// WEBPACK FOOTER //
// ./src/apps/profile_questions/util/helpers.js